// The TUNE MAP: a chart's chord lane as a sequence of cadence CELLS, each a
// shape written in roman numerals inside its own key, that key named as an
// interval from the home key. The memorisation method behind it (a Marbin
// lesson, refined with the user, Oct 2026): everything is relative to home, so
// a tune becomes shapes that transpose. A cell reads
//
//   ii–V–I ↑P4            the shape (numerals INSIDE the cell's key) and the
//                         key as a signed interval from home (R = home);
//   iv ♭VII7 R backdoor   plus an optional NAME for a recurring device.
//
// The I of a cadence is the key the interval names, which is also the soloing
// key. Rules the user settled: a landing is never a scale degree of home
// ("♭VII" is not a key), only an interval; a ii–V with no I is just "ii–V"
// (never "no landing"), and a view says "lean into" the next landing for such
// an APPROACH cell; home-frame material (plain diatonic chords, dominant
// secondary-dominant chains, pedal vamps) reads as numerals of home at R. Everything left over is
// a single chord in home's frame, so a tune with no cadences degrades to its
// chords and never to invented cells. Prior art: Nashville numbers, Conrad
// Cork's LEGO bricks, Impro-Visor's roadmaps.
//
// Pure: a chart in (a Score through scoreToChart), cells out, positioned in
// WRITTEN order so a chart surface draws a bracket over each cell's bars.

import { analyzeHarmony, keyRegions } from './analysis';
import { flatMeasuresToSequence, measureStartBeats, sequenceChordIndex } from './chart';
import type { ChordTypeId } from './chords';
import { degreeLabel, qualityFamily } from './corpus';
import type { IRealChart, IRealMeasure } from './ireal/types';
import { pitchClassOf, conventionalTonicName } from './notes';
import { transitionBits } from './reharm/surprisal';
import type { SequenceChord } from './song';

const mod12 = (n: number) => ((n % 12) + 12) % 12;

// ---------------------------------------------------------------------------
// Intervals from home
// ---------------------------------------------------------------------------

/** Semitones above home → the interval as the method names it. Fourths and
 *  fifths always read UPWARD (the cycle: "up a fourth", "up a fifth"), the
 *  rest the nearer way, the tritone as an augmented fourth. */
export const INTERVAL_LABELS = [
  'R',
  '↑m2',
  '↑M2',
  '↑m3',
  '↑M3',
  '↑P4',
  '↑A4',
  '↑P5',
  '↓M3',
  '↓m3',
  '↓M2',
  '↓m2',
] as const;
export type IntervalLabel = (typeof INTERVAL_LABELS)[number];

export function intervalLabel(homePc: number, tonicPc: number): IntervalLabel {
  return INTERVAL_LABELS[mod12(tonicPc - homePc)];
}

/** Where the cell's key sits on the fretboard relative to home's root, in
 *  standard tuning: the hint an interval chip shows on tap or hover. */
export const FRETBOARD_HINTS: Record<IntervalLabel, string> = {
  R: 'home, the same spot',
  '↑m2': 'one fret up, same string',
  '↑M2': 'two frets up, same string',
  '↑m3': 'three frets up, same string',
  '↑M3':
    'four frets up, same string; or next string up, one fret back (the same fret if that string is the B)',
  '↑P4': 'same fret, next string up, one fret higher if that string is the B',
  '↑A4': 'next string up, one fret higher, two if that string is the B',
  '↑P5': 'next string up, two frets higher, three if that string is the B',
  '↓M3': 'four frets back, same string',
  '↓m3': 'three frets back, same string',
  '↓M2': 'two frets back, same string',
  '↓m2': 'one fret back, same string',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TuneMapCellKind =
  | 'ii-V'
  | 'V-I'
  | 'backdoor'
  | 'secondary'
  | 'vamp'
  | 'diatonic'
  | 'chord';
export type TuneMapCellName = 'slide' | 'backdoor' | 'minor' | 'sub' | 'secondary' | 'vamp';

export interface TuneMapKey {
  tonicPc: number;
  minor: boolean;
  /** "E♭", "C minor". */
  label: string;
}

export interface TuneMapChord {
  /** The CHART chord's id (`measure.chords[chordIndex].id`), which is what a
   *  surface drawing the written chords keys by. */
  id: string;
  /** The written index in its bar's `chords`, N.C. counted. */
  chordIndex: number;
  symbol: string;
  root: string;
  chordType: ChordTypeId;
  bass?: string;
  /** Absolute beats from the form's start; `end` exclusive. */
  start: number;
  end: number;
  /** Written bar index of the onset. */
  bar: number;
  /** Which of the cell's merged events the chord belongs to (a restated
   *  chord shares its event), so a run written "×n" splits into its
   *  repetitions. */
  event: number;
}

export interface TuneMapCell {
  id: string;
  kind: TuneMapCellKind;
  /** The shape in numerals inside the cell's key: "ii–V–I", "iv ♭VII7 I", "I ii iii ii". */
  shape: string;
  /** The cell's key as an interval from home. */
  interval: IntervalLabel;
  /** The cell's key, the soloing key where the cell lands. */
  key: TuneMapKey;
  name?: TuneMapCellName;
  /** No landing: a view says "lean into" the next landing instead of a key. */
  approach: boolean;
  /** The pedal under a vamp, as a degree of home ("5") and a note ("B♭"). */
  pedal?: { pc: number; degree: string; note: string };
  /** The count a long run's unit repeats, when `shape` writes "… ×n". */
  times?: number;
  /** Absolute beats; `end` exclusive. */
  start: number;
  end: number;
  /** Written bar indexes, inclusive. */
  bars: [number, number];
  chords: TuneMapChord[];
  /** The next cell that lands (round the form), for an approach cell. */
  leansInto?: number;
}

export interface TuneMapMemorability {
  /** Surprise of the distinct transitions per written bar (bits). */
  bitsPerBar: number;
  totalBits: number;
  /** Distinct roman transitions. */
  transitions: number;
  model: 'surprisal';
}

export interface TuneMap {
  home: TuneMapKey;
  cells: TuneMapCell[];
  /** Absolute beat each written bar starts and ends at. */
  barStarts: number[];
  barEnds: number[];
  /** Distinct keys the LANDING cells name: the keys to solo in. */
  keys: number;
  /** Distinct keys the approach cells lean from. */
  approaches: number;
  devices: TuneMapCellName[];
  bars: number;
  summary: string;
  /** Null for a chart with fewer than two chords. */
  memorability: TuneMapMemorability | null;
}

// ---------------------------------------------------------------------------
// Events: the chord lane with restatements merged
// ---------------------------------------------------------------------------

type Family = ReturnType<typeof qualityFamily>;

interface Event {
  chords: TuneMapChord[];
  rootPc: number;
  bassPc: number;
  family: Family;
  chordType: ChordTypeId;
  start: number;
  end: number;
  bar: number;
  /** Nothing reads across into this event: a repeat or ending boundary, a
   *  gap of N.C. bars, the end of a cell that cannot continue. */
  breakBefore: boolean;
  /** The event opens a bar carrying a section letter. */
  sectionStart: boolean;
}

const DEGREES = ['1', '♭2', '2', '♭3', '3', '4', '♭5', '5', '♭6', '6', '♭7', '7'];

function prettyNote(note: string): string {
  return note.replace(/b/g, '♭').replace(/#/g, '♯');
}

function keyOf(tonicPc: number, minor: boolean, spelling?: string): TuneMapKey {
  const name = prettyNote(spelling ?? conventionalTonicName(tonicPc, minor ? 'minor' : 'major'));
  return { tonicPc, minor, label: minor ? `${name} minor` : name };
}

/** A written bar nothing reads INTO from the bar before it: the bar after a
 *  closing repeat or final barline, or the first bar of a new ending. The
 *  analysis runs in written order, and across these the written neighbour is
 *  not the performed one. */
function barBreaks(measures: IRealMeasure[]): boolean[] {
  return measures.map((m, i) => {
    const prev = measures[i - 1];
    if (!prev) return false;
    if (prev.close === 'repeat' || prev.close === 'final') return true;
    return m.ending != null && m.ending !== prev.ending;
  });
}

/** Consecutive identical chords (a simile bar, an anticipation restated on
 *  the barline, a chord held across bars) are ONE harmonic event. */
function mergeEvents(
  sequence: SequenceChord[],
  measures: IRealMeasure[],
  barStarts: number[],
): Event[] {
  const breaks = barBreaks(measures);
  const events: Event[] = [];
  for (const chord of sequence) {
    const rootPc = pitchClassOf(chord.root);
    const bassPc = chord.bass ? pitchClassOf(chord.bass) : rootPc;
    const start = barStarts[chord.barIndex] + chord.beat - 1;
    const end = start + chord.durationBeats;
    const chordIndex = sequenceChordIndex(chord.id) ?? 0;
    const entry: TuneMapChord = {
      id: measures[chord.barIndex]?.chords[chordIndex]?.id ?? chord.id,
      chordIndex,
      symbol: chord.symbol,
      root: chord.displayRoot,
      chordType: chord.chordType,
      ...(chord.bass ? { bass: chord.bass } : {}),
      start,
      end,
      bar: chord.barIndex,
      event: 0,
    };
    const last = events[events.length - 1];
    const opensBar = chord.beat === 1;
    const breakBefore = !last || last.end < start - 1e-9 || (opensBar && breaks[chord.barIndex]);
    if (
      last &&
      !breakBefore &&
      last.rootPc === rootPc &&
      last.bassPc === bassPc &&
      last.chordType === chord.chordType
    ) {
      last.end = end;
      last.chords.push(entry);
      continue;
    }
    events.push({
      chords: [entry],
      rootPc,
      bassPc,
      family: qualityFamily(chord.chordType),
      chordType: chord.chordType,
      start,
      end,
      bar: chord.barIndex,
      breakBefore: !!last && breakBefore,
      sectionStart: opensBar && !!measures[chord.barIndex]?.section,
    });
  }
  return events;
}

// ---------------------------------------------------------------------------
// Numerals
// ---------------------------------------------------------------------------

const lower = (degree: string) => degree.replace(/[IV]+/g, (m) => m.toLowerCase());

/** A chord as a numeral of `tonicPc`: uppercase major, lowercase minor, "7" on
 *  a dominant, "ø" and "°" on the diminished families, "sus" and "+" where the
 *  catalogue says so. The way a lead sheet writes a roman, with no "∆". */
export function chordNumeral(rootPc: number, chordType: ChordTypeId, tonicPc: number): string {
  const degree = degreeLabel(rootPc, tonicPc);
  if (chordType === 'dom7sus4') return `${degree}sus`;
  if (chordType === 'aug') return `${degree}+`;
  switch (qualityFamily(chordType)) {
    case '∆':
      return degree;
    case 'm':
      return lower(degree);
    case '7':
      return `${degree}7`;
    case 'ø':
      return `${lower(degree)}ø`;
    case '°':
      return `${lower(degree)}°`;
  }
}

const eventNumeral = (event: Event, tonicPc: number) =>
  chordNumeral(event.rootPc, event.chordType, tonicPc);

/** A degree's numeral in home's diatonic case: ii, iii, vi and vii lower in a
 *  major home, i, ii and iv in a minor one; a chromatic degree upper. */
function diatonicNumeral(pc: number, home: TuneMapKey): string {
  const degree = mod12(pc - home.tonicPc);
  const label = degreeLabel(pc, home.tonicPc);
  const lowered = home.minor ? [0, 2, 5] : [2, 4, 9, 11];
  return lowered.includes(degree) ? lower(label) : label;
}

/** A dominant as the V of what it points at, a fifth below: G7 in E♭ is V/vi,
 *  C7 V/ii, D♭7 V/♭III. Home's own V7 stays V7, and a dominant a tritone from
 *  home is the sub of that V, ♭II7. Anything but a dominant keeps its numeral. */
function secondaryNumeral(event: Event, home: TuneMapKey): string {
  if (event.family !== '7') return eventNumeral(event, home.tonicPc);
  const degree = mod12(event.rootPc - home.tonicPc);
  if (degree === 7) return 'V7';
  if (degree === 1) return '♭II7';
  return `V/${diatonicNumeral(mod12(event.rootPc + 5), home)}`;
}

/** Home's own chords, whose runs are home-frame cells: the diatonic sevenths,
 *  plus the blues tonics I7 and IV7 in a major home. Minor admits the dorian
 *  ii and IV and the harmonic V beside the natural set. */
function isHomeChord(event: Event, home: TuneMapKey): boolean {
  const degree = mod12(event.rootPc - home.tonicPc);
  const f = event.family;
  if (!home.minor) {
    switch (degree) {
      case 0:
      case 5:
        return f === '∆' || f === '7';
      case 2:
      case 4:
      case 9:
        return f === 'm';
      case 7:
        return f === '7';
      case 11:
        return f === 'ø';
      default:
        return false;
    }
  }
  switch (degree) {
    case 0:
      return f === 'm';
    case 2:
      return f === 'ø' || f === 'm';
    case 3:
    case 8:
      return f === '∆';
    case 5:
      return f === 'm' || f === '∆';
    case 7:
      return f === '7' || f === 'm';
    case 10:
      return f === '7' || f === '∆';
    case 11:
      return f === '°';
    default:
      return false;
  }
}

const sameChord = (a: Event, b: Event) =>
  a.rootPc === b.rootPc && a.bassPc === b.bassPc && a.family === b.family;

/** A run of at least this many chords is written as its repeating unit. */
const COMPRESS_FROM = 6;

/**
 * The shape of a run of home-frame chords: its numerals, or, for a long run
 * that repeats a unit exactly ("i IV" eight times), the unit and a count.
 */
function runShape(run: Event[], tonicPc: number): { shape: string; times?: number } {
  const numerals = run.map((e) => eventNumeral(e, tonicPc));
  const n = run.length;
  if (n >= COMPRESS_FROM) {
    for (let p = 1; p <= n / 2; p++) {
      if (n % p !== 0) continue;
      let periodic = true;
      for (let j = p; j < n && periodic; j++) periodic = sameChord(run[j], run[j - p]);
      if (periodic) {
        return { shape: `${numerals.slice(0, p).join(' ')} ×${n / p}`, times: n / p };
      }
    }
  }
  return { shape: numerals.join(' ') };
}

// ---------------------------------------------------------------------------
// The home key
// ---------------------------------------------------------------------------

function declaredKey(key: string | undefined): TuneMapKey | null {
  const trimmed = (key ?? '').trim();
  const m = /^([A-Ga-g][#b♯♭]?)\s*(-|m|min|minor|major|maj)?$/i.exec(trimmed);
  if (!m) return null;
  const tonic = m[1][0].toUpperCase() + m[1].slice(1).replace('♯', '#').replace('♭', 'b');
  const mode = (m[2] ?? '').toLowerCase();
  const minor = mode === '-' || mode === 'm' || mode === 'min' || mode === 'minor';
  try {
    return keyOf(pitchClassOf(tonic), minor, tonic);
  } catch {
    return null;
  }
}

/**
 * The declared key, read as the analysis lane reads it: a MAJOR key stamped on
 * a form that opens and closes on the relative minor's tonic is that minor key
 * (a key signature cannot say "C minor"). Without a declared key, the key
 * region covering most of the form.
 */
function homeKeyOf(chart: IRealChart, sequence: SequenceChord[], events: Event[]): TuneMapKey {
  const declared = declaredKey(chart.key);
  if (declared) {
    if (!declared.minor && events.length > 1) {
      const relative = mod12(declared.tonicPc + 9);
      const first = events[0];
      const last = events[events.length - 1];
      if (
        first.rootPc === relative &&
        first.family === 'm' &&
        last.rootPc === relative &&
        last.family === 'm'
      ) {
        return keyOf(relative, true);
      }
    }
    return declared;
  }
  if (sequence.length === 0) return keyOf(0, false);
  const regions = keyRegions(sequence);
  let best = regions[0];
  for (const region of regions) {
    if (region.to - region.from > best.to - best.from) best = region;
  }
  return keyOf(best.tonicPc, best.minor);
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

interface Draft {
  kind: TuneMapCellKind;
  shape: string;
  key: TuneMapKey;
  name?: TuneMapCellName;
  approach: boolean;
  pedal?: TuneMapCell['pedal'];
  times?: number;
  events: Event[];
  /** The ii's root, for the slide rule. */
  iiPc?: number;
  /** A bare ii–V with no landing, which the slide rule may name. */
  slidable?: boolean;
}

const isMinorFamily = (f: Family) => f === 'm' || f === 'ø';

/** Root motion that chains one dominant to the next: down a fifth, down a
 *  half step (a sub resolving) or down a whole step (parallel dominants,
 *  E♭7 D♭7 | Cm). A tritone is a substitute, not a step, so D♭7 | G7 C7 is
 *  two run-ups. */
const CHAIN_LINKS = new Set([5, 11, 10]);

export function tuneMap(chart: IRealChart): TuneMap {
  const sequence = flatMeasuresToSequence(chart.measures, 'middle', chart.timeSignature);
  const barStarts = measureStartBeats(chart.measures, chart.timeSignature);
  const barEnds = barStarts.map((start, i) => {
    if (i + 1 < barStarts.length) return barStarts[i + 1];
    const meter = chart.measures.reduce<[number, number]>(
      (acc, bar) => bar.timeSig ?? acc,
      chart.timeSignature,
    );
    return start + (chart.measures[i]?.pickupBeats ?? meter[0]);
  });
  const events = mergeEvents(sequence, chart.measures, barStarts);
  const home = homeKeyOf(chart, sequence, events);
  const homePc = home.tonicPc;
  const covered = new Set<number>();
  const drafts: { at: number; draft: Draft }[] = [];
  const push = (at: number, draft: Draft) => {
    drafts.push({ at, draft });
    for (const event of draft.events) covered.add(events.indexOf(event));
  };
  const up = (a: Event, b: Event) => mod12(b.rootPc - a.rootPc);
  const free = (i: number) => i >= 0 && i < events.length && !covered.has(i);
  /** The event that follows `i` in the music, or -1 at a boundary. */
  const after = (i: number) => (i + 1 < events.length && !events[i + 1].breakBefore ? i + 1 : -1);

  interface IiV {
    a: Event;
    b: Event;
    bi: number;
    ci: number;
    sub: boolean;
    tonicPc: number;
    landing: Event | null;
  }
  // A ii–V starts here: a minor-family chord and a dominant a fourth up —
  // or, the tritone sub, a half step down, which counts only when it LANDS a
  // half step below (Cm7 B7 | B♭m7 is a landing on Cm7 and a chromatic B7,
  // not a ii–♭II7).
  const iiVAt = (i: number): IiV | null => {
    const bi = after(i);
    if (!free(i) || !free(bi)) return null;
    const a = events[i];
    const b = events[bi];
    if (!isMinorFamily(a.family) || b.family !== '7') return null;
    const motion = up(a, b);
    if (motion !== 5 && motion !== 11) return null;
    const sub = motion === 11;
    const tonicPc = mod12(b.rootPc + (sub ? 11 : 5));
    const ci = after(bi);
    const landing = landingAt(ci, tonicPc);
    if (sub && !landing) return null;
    return { a, b, bi, ci, sub, tonicPc, landing };
  };
  const startsIiV = (i: number): boolean => iiVAt(i) !== null;
  // A landing: a major or minor chord on the cadence's tonic that is not the
  // ii of a ii–V of its own (Gm7 C7 | Fm7 B♭7: the Fm7 belongs to its ii–V, so
  // the pair before it is no cadence into F minor). A dominant lands only on
  // home's own tonic, the blues I7.
  function landingAt(i: number, tonicPc: number): Event | null {
    if (!free(i)) return null;
    const c = events[i];
    if (c.rootPc !== tonicPc) return null;
    if (c.family === '7') return tonicPc === homePc ? c : null;
    if (c.family !== '∆' && c.family !== 'm') return null;
    if (startsIiV(i)) return null;
    return c;
  }
  /** A run from `i` of free events passing `admit`, inside one section. */
  const runFrom = (i: number, admit: (e: Event) => boolean): Event[] => {
    const run: Event[] = [];
    for (let j = i; free(j) && admit(events[j]); j = after(j)) {
      if (j !== i && events[j].sectionStart) break;
      run.push(events[j]);
    }
    return run;
  };

  // 1. Vamps: a run over one fixed bass, slash chords over the pedal (a chord
  //    on its own root may sit between them, but a dominant on its root is a
  //    dominant, not a pedal chord), at least two slashes and three chords,
  //    cut to whole repeats of its unit.
  for (let i = 0; i < events.length; i++) {
    if (covered.has(i)) continue;
    const pedalPc = events[i].bassPc;
    const run = runFrom(
      i,
      (e) => e.bassPc === pedalPc && !(e.rootPc === pedalPc && e.family === '7'),
    );
    const slashes = run.filter((e) => e.rootPc !== pedalPc).length;
    if (run.length < 3 || slashes < 2) continue;
    const first = run[0].chords[0];
    push(i, {
      kind: 'vamp',
      ...runShape(run, homePc),
      key: home,
      name: 'vamp',
      approach: false,
      pedal: {
        pc: pedalPc,
        degree: DEGREES[mod12(pedalPc - homePc)],
        note: prettyNote(first.bass ?? first.root),
      },
      events: run,
    });
    i += run.length - 1;
  }

  // 2. The ii–V family: landing, the backdoor, chained into the next ii, or bare.
  for (let i = 0; i < events.length; i++) {
    const found = iiVAt(i);
    if (!found) continue;
    const { a, b, ci, sub, tonicPc, landing } = found;
    const ii = a.family === 'ø' ? 'iiø' : 'ii';
    const v = sub ? '♭II7' : 'V';
    if (landing) {
      const minor = landing.family === 'm';
      // The minor cadence is written iiø–V–i whatever the chart gives the ii
      // (Moment's Notice's Dm7 G7 | Cm7 reads iiø–V–i): the shape names the
      // cadence, the chords row shows what is written.
      push(i, {
        kind: 'ii-V',
        shape: `${minor ? 'iiø' : ii}–${v}–${minor ? 'i' : 'I'}`,
        key: keyOf(tonicPc, minor),
        name: sub ? 'sub' : minor ? 'minor' : undefined,
        approach: false,
        events: [a, b, landing],
        iiPc: a.rootPc,
      });
      continue;
    }
    // The backdoor: home's iv and ♭VII7, resolving to home's I or not.
    if (
      !sub &&
      !home.minor &&
      a.family === 'm' &&
      a.rootPc === mod12(homePc + 5) &&
      b.rootPc === mod12(homePc + 10)
    ) {
      const resolves = landingAt(ci, homePc);
      const resolvesHome = resolves && resolves.family === '∆' ? resolves : null;
      push(i, {
        kind: 'backdoor',
        shape: resolvesHome ? 'iv ♭VII7 I' : 'iv ♭VII7',
        key: home,
        name: 'backdoor',
        approach: !resolvesHome,
        events: resolvesHome ? [a, b, resolvesHome] : [a, b],
      });
      continue;
    }
    // A ii–V on one of home's own chords whose V runs by a fifth into the ii of
    // the NEXT ii–V is a secondary dominant in home's frame (Gm7 C7 | Fm7 B♭7
    // reads iii V/ii, not a cadence into F minor), as a dominant chain reads. A foreign ii–V chain keeps each ii–V in its own key.
    if (
      !sub &&
      tonicPc !== homePc &&
      isHomeChord(a, home) &&
      ci >= 0 &&
      up(b, events[ci]) === 5 &&
      startsIiV(ci)
    ) {
      push(i, {
        kind: 'secondary',
        shape: `${eventNumeral(a, homePc)} ${secondaryNumeral(b, home)}`,
        key: home,
        name: 'secondary',
        approach: true,
        events: [a, b],
      });
      continue;
    }
    const minor = a.family === 'ø';
    push(i, {
      kind: 'ii-V',
      shape: `${ii}–${v}`,
      key: keyOf(tonicPc, minor),
      name: sub ? 'sub' : minor ? 'minor' : undefined,
      approach: true,
      events: [a, b],
      iiPc: a.rootPc,
      slidable: true,
    });
  }

  // 3. Dominants left over, home's own excepted (I7, IV7, V7 are home chords):
  //    a lone one landing by a fifth or a half step is a V–I; anything else is
  //    a chain of secondary dominants (written V/x by what each points at),
  //    chained by fifths, half steps and whole steps — through
  //    home's V7 where the chain reaches it (the rhythm changes bridge is
  //    III7 VI7 II7 V7), which never starts a chain of its own.
  const homeV = mod12(homePc + 7);
  for (let i = 0; i < events.length; i++) {
    if (!free(i) || events[i].family !== '7' || isHomeChord(events[i], home)) continue;
    const chain: Event[] = [events[i]];
    let j = after(i);
    while (
      free(j) &&
      events[j].family === '7' &&
      (!isHomeChord(events[j], home) || events[j].rootPc === homeV) &&
      CHAIN_LINKS.has(up(chain[chain.length - 1], events[j]))
    ) {
      chain.push(events[j]);
      j = after(j);
    }
    const last = chain[chain.length - 1];
    const motion = j >= 0 ? up(last, events[j]) : -1;
    if (chain.length === 1 && (motion === 5 || motion === 11)) {
      const tonicPc = mod12(last.rootPc + motion);
      const landing = landingAt(j, tonicPc);
      if (landing) {
        const minor = landing.family === 'm';
        const sub = motion === 11;
        push(i, {
          kind: 'V-I',
          shape: `${sub ? '♭II7' : 'V'}–${minor ? 'i' : 'I'}`,
          key: keyOf(tonicPc, minor),
          name: sub ? 'sub' : undefined,
          approach: false,
          events: [last, landing],
        });
        continue;
      }
    }
    push(i, {
      kind: 'secondary',
      shape: chain.map((e) => secondaryNumeral(e, home)).join(' '),
      key: home,
      name: 'secondary',
      approach: true,
      events: chain,
    });
    i = events.indexOf(last);
  }

  // 4. What is left: runs of home's own chords as one cell (cut to whole
  //    repeats of a unit), anything else a single chord in home's frame.
  for (let i = 0; i < events.length; i++) {
    if (!free(i)) continue;
    if (isHomeChord(events[i], home)) {
      const run = runFrom(i, (e) => isHomeChord(e, home));
      push(i, {
        kind: 'diatonic',
        ...runShape(run, homePc),
        key: home,
        approach: false,
        events: run,
      });
      i += run.length - 1;
      continue;
    }
    push(i, {
      kind: 'chord',
      shape: eventNumeral(events[i], homePc),
      key: home,
      approach: false,
      events: [events[i]],
    });
  }

  drafts.sort((x, y) => x.at - y.at);
  // The slide: a bare ii–V a half step below the ii–V that follows it.
  drafts.forEach(({ draft }, n) => {
    const next = drafts[n + 1]?.draft;
    if (
      draft.slidable &&
      draft.iiPc != null &&
      next?.iiPc != null &&
      !next.events[0].breakBefore &&
      mod12(next.iiPc - draft.iiPc) === 1
    ) {
      draft.name = 'slide';
    }
  });

  const cells: TuneMapCell[] = drafts.map(({ draft }, n) => {
    const first = draft.events[0];
    const last = draft.events[draft.events.length - 1];
    const lastChord = last.chords[last.chords.length - 1];
    return {
      id: `cell-${n}`,
      kind: draft.kind,
      shape: draft.shape,
      interval: intervalLabel(homePc, draft.key.tonicPc),
      key: draft.key,
      ...(draft.name ? { name: draft.name } : {}),
      approach: draft.approach,
      ...(draft.pedal ? { pedal: draft.pedal } : {}),
      ...(draft.times ? { times: draft.times } : {}),
      start: first.start,
      end: last.end,
      bars: [first.bar, lastChord.bar],
      chords: draft.events.flatMap((e, k) => e.chords.map((c) => ({ ...c, event: k }))),
    };
  });
  // An approach cell leans into the next cell that lands, round the form: a
  // cadence with its I, or a home-frame cell opening on home's tonic.
  const lands = cells.map(
    (c) =>
      (!c.approach && (c.kind === 'ii-V' || c.kind === 'V-I' || c.kind === 'backdoor')) ||
      ((c.kind === 'diatonic' || c.kind === 'vamp' || c.kind === 'chord') &&
        /^(I|i)(7|$|\s)/.test(c.shape)),
  );
  cells.forEach((cell, n) => {
    if (!cell.approach) return;
    for (let k = 1; k <= cells.length; k++) {
      const m = (n + k) % cells.length;
      if (lands[m]) {
        cell.leansInto = m;
        return;
      }
    }
  });

  // The keys are the LANDINGS' (a cadence with its I, home's own chords, a
  // vamp): the keys a soloist plays in. An approach cell (a slide, a chain,
  // a backdoor that runs on) names the key it leans from, counted apart.
  const keyId = (c: TuneMapCell) => `${c.key.tonicPc}:${c.key.minor}`;
  const keys = new Set(cells.filter((c) => !c.approach).map(keyId));
  const approaches = new Set(cells.filter((c) => c.approach).map(keyId));
  const devices = [...new Set(cells.flatMap((c) => (c.name ? [c.name] : [])))];
  const bars = chart.measures.length;
  const memorability = memorabilityOf(sequence, chart.key, bars);
  const summary = summarise(
    formLetters(chart),
    keys.size,
    approaches.size,
    devices,
    bars,
    memorability,
    cells.length > 0,
  );
  return {
    home,
    cells,
    barStarts,
    barEnds,
    keys: keys.size,
    approaches: approaches.size,
    devices,
    bars,
    summary,
    memorability,
  };
}

// ---------------------------------------------------------------------------
// Memorability: the corpus surprisal model over the local-key romans
// ---------------------------------------------------------------------------

/**
 * How much there is to remember: the surprise of the tune's chord transitions
 * under the corpus bigram model (surprisal.ts, −log2 P(next | prev) over the
 * local-key romans the analysis lane writes), per written bar. Each DISTINCT
 * transition is charged once, since a step learned once is known on every
 * repeat: a modal vamp of two chords costs almost nothing however long it
 * runs, a form of ii–V–Is through many keys costs every step. Lower is
 * easier to remember. Null with fewer than two chords.
 */
export function memorabilityOf(
  sequence: SequenceChord[],
  key: string | undefined,
  bars: number,
): TuneMapMemorability | null {
  if (sequence.length < 2 || bars === 0) return null;
  const romans = analyzeHarmony(sequence, key).romans.filter(Boolean);
  if (romans.length < 2) return null;
  const seen = new Set<string>();
  let totalBits = 0;
  for (let i = 1; i < romans.length; i++) {
    const step = `${romans[i - 1]}→${romans[i]}`;
    if (seen.has(step)) continue;
    seen.add(step);
    totalBits += transitionBits(romans[i - 1], romans[i]);
  }
  return {
    bitsPerBar: totalBits / bars,
    totalBits,
    transitions: seen.size,
    model: 'surprisal',
  };
}

export const DEVICE_LABELS: Record<TuneMapCellName, string> = {
  slide: 'slide',
  backdoor: 'backdoor',
  minor: 'minor',
  sub: 'tritone sub',
  secondary: 'sec. dom.',
  vamp: 'vamp',
};

/** The chart's section letters in order, "AABA"; a multi-letter name keeps
 *  its spaces ("Intro A B A"); empty when the chart marks no sections. */
function formLetters(chart: IRealChart): string {
  const labels = chart.measures.flatMap((m) => (m.section ? [m.section] : []));
  if (!labels.length) return '';
  return labels.every((l) => l.length === 1) ? labels.join('') : labels.join(' ');
}

function summarise(
  form: string,
  keys: number,
  approaches: number,
  devices: TuneMapCellName[],
  bars: number,
  memorability: TuneMapMemorability | null,
  anyCells: boolean,
): string {
  if (!anyCells) return 'No chords to map';
  const parts = form ? [form] : [];
  parts.push(`${keys} ${keys === 1 ? 'key' : 'keys'}`);
  if (approaches) parts.push(`${approaches} ${approaches === 1 ? 'approach' : 'approaches'}`);
  parts.push(
    devices.length ? devices.map((d) => DEVICE_LABELS[d]).join(', ') : 'no devices',
    `${bars} ${bars === 1 ? 'bar' : 'bars'}`,
  );
  if (memorability) parts.push(`${memorability.bitsPerBar.toFixed(1)} bits a bar`);
  return parts.join(' · ');
}

/** The chords a cell's check row prints, in order. */
export function cellChordsLabel(cell: TuneMapCell): string {
  return cell.chords
    .map((c) => c.symbol)
    .filter((symbol, i, all) => i === 0 || symbol !== all[i - 1])
    .join(' ');
}

/** One line per cell: "shape interval name (chords)" — the test and debug form. */
export function cellLine(cell: TuneMapCell): string {
  const words = [cell.shape, cell.interval];
  if (cell.name) words.push(cell.name);
  if (cell.pedal) words.push(`/${cell.pedal.degree}`);
  return `${words.join(' ')} (${cellChordsLabel(cell)})`;
}
